<template>
    <b-modal :active.sync="show" id="endContractModal" aria-modal :width="600" :can-cancel="false">
        <div class="modal-card" style="width: auto">
            <b-loading :active.sync="loading" :is-full-page="false"></b-loading>

            <header class="modal-card-head"><p class="modal-card-title">{{ showAccessMessage ? 'Toegang controleren' : 'Contract beëindigen' }}</p></header>
            <section class="modal-card-body" style="overflow: hidden;border-radius:0;">
                <div v-if="showAccessMessage">
                    <p>Voor deze gebruiker is toegang aangemaakt. Wil je de toegang controleren?</p>
                </div>
                <div v-else>
                    <p>Na het veranderen van de einddatum of het direct beëindigen van het contract ontvangt de klant een e-mail.</p>

                    <b-checkbox v-model="perDirect">Contract per direct beëindigen.</b-checkbox>

                    <div v-show="!perDirect && possibleDates">
                        <br>
                        <p>Contract beëindigen na datum:</p>

                        <b-field label="Datum" v-if="possibleDates.length > 0">
                            <b-select placeholder="Selecteer een datum" v-model="endDate" expanded>
                                <option v-for="(date, index) in possibleDates" :value="date" :key="'date_'+index">
                                    {{ moment(date).format('DD-MM-YYYY') }}
                                </option>
                            </b-select>
                        </b-field>
                        <div v-else>
                            <p>Er zijn geen mogelijke einddatums gevonden. Het contract wordt al beëindigd op {{ moment(record.step1.end_date).format('DD-MM-YYYY') }}.</p>
                        </div>
                    </div>

                    <br>
                    <br>

                    <label>Facturen annuleren (optioneel):</label><br><br>
                    <b-checkbox v-model="cancelAllInvoices" :disabled="unpaidInvoices === 0">Annuleer alle niet betaalde facturen ({{ unpaidInvoices }} gevonden).</b-checkbox>
                    <b-checkbox v-model="cancelInvoices" :disabled="unpaidInvoicesFromDate === 0">Annuleer alle niet betaalde facturen vanaf <span>{{ moment(endDate).format('DD-MM-YYYY') }}</span> ({{ unpaidInvoicesFromDate }} gevonden).</b-checkbox>
                </div>
            </section>
            <footer class="modal-card-foot text-right">
                <button class="button" type="button" @click="$emit('closeEndContractModal')">Annuleren</button>
                <router-link class="button is-success" v-if="showAccessMessage" type="button" :to="{ name: 'ConnectRecord', params: {id: hasAccess} }">Toegang controleren</router-link>
                <button v-else class="button is-success" @click="endContract" :disabled="!perDirect && !endDate">Bevestigen</button>
            </footer>
        </div>
    </b-modal>
</template>

<script>
import toastMixins from '@/mixins/toastMixins.js'

export default {
    name: 'ContractsRecordEndContractModal',

    mixins: [toastMixins],

    props: {
        show: {
            type: Boolean,
            required: true,
            default: false
        },
        record: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            loading: false,
            perDirect: true,
            endDate: moment(new Date()).format('YYYY-MM-DD'),
            cancelInvoices: true,
            cancelAllInvoices: false,
            possibleDates: [],

            unpaidInvoices: 0,
            unpaidInvoicesFromDate: 0,

            hasAccess: false,
            showAccessMessage: false
        }
    },

    computed: {
        invoiceType() {
            return moment(new Date()).format('D') < 15 ? 'pre15' : 'after15'
        }
    },

    methods: {
        setDates(){
            // Create possible dates
            let dates = []
            const frequency = this.record.step1.invoice_freq
            const monthsToAdd = frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1
            const today = moment().startOf('day')

            // Only use contract start date if it's in current month
            const contractStartDate = moment(this.record.step1.start_date)
            const isStartDateCurrentMonth = contractStartDate.isSame(today, 'month')

            // Determine if we should use pre15 or after15 logic
            let isPre15
            if (isStartDateCurrentMonth) {
                isPre15 = contractStartDate.date() < 15
            } else {
                isPre15 = today.date() < 15
            }

            // Set base date to today
            let baseDate = today.clone()

            // For pre15: First period ends at end of current month
            // For after15: First period ends at end of next month
            if (isPre15) {
                baseDate = baseDate.endOf('month')
            } else {
                baseDate = baseDate.add(1, 'month').endOf('month')
            }

            // Generate next 12 periods based on frequency
            for (let i = 0; i < 12; i++) {
                // Calculate the date by adding months and then set it to the end of that month
                let date = baseDate.clone().add(i * monthsToAdd, 'months').endOf('month');

                // If end_date is set, only include dates before it
                if (!_.isNull(this.record.step1.end_date)) {
                    if (date.format('YYYYMMDD') < moment(this.record.step1.end_date).format('YYYYMMDD')) {
                        dates.push(date.format('YYYY-MM-DD'))
                    }
                } else {
                    dates.push(date.format('YYYY-MM-DD'))
                }
            }

            console.log('Debug - Possible Dates:', {
                frequency,
                monthsToAdd,
                today: today.format('YYYY-MM-DD'),
                isPre15,
                datesCount: dates.length,
                dates: dates.map(d => moment(d).format('YYYY-MM-DD')),
                contractEndDate: this.record.step1.end_date ? moment(this.record.step1.end_date).format('YYYY-MM-DD') : null
            })

            this.possibleDates = dates

            // Set first possibility
            if(!this.perDirect){
                this.endDate = this.possibleDates[0]
            }

            // Set default end date (if set and found in coming periods)
            if(!_.isNull(this.record.step1.end_date) && !this.perDirect){
                const indexOfEndDate = dates.indexOf(moment(this.record.step1.end_date).format('YYYY-MM-DD'))
                if(indexOfEndDate !== -1){
                    this.endDate = moment(this.record.step1.end_date).format('YYYY-MM-DD')
                }
            }

            this.getUnpaidInvoices()
        },

        getUnpaidInvoices() {
            this.loading = true

            axios.get('/api/contract/'+this.$route.params.id+'/unpaid-invoices?date='+this.endDate, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                this.unpaidInvoices = response.data.total
                this.unpaidInvoicesFromDate = response.data.fromDate
                this.hasAccess = response.data.access

                if(response.data.fromDate == 0){
                    this.cancelInvoices = false
                }
            }).catch((error) => {
                console.error(error)
                this.showError('Daar gaat iets fout! Probeer het opnieuw!')
                this.$emit('closeEndContractModal')
            }).then(() => {
                this.loading = false
            })
        },

        endContract(){
            this.loading = true

            axios.post('/api/contract/'+this.$route.params.id+'/end-contract',
            {
                perDirect: this.perDirect,
                endDate: this.endDate,
                cancelInvoices: this.cancelInvoices,
                cancelAllInvoices: this.cancelAllInvoices,
            }, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                if(response.data == 'contract-already-finalized'){
                    this.showError('Contract is al afgesloten!')
                    this.$emit('closeEndContractModal')
                    return
                }

                this.showMessage(this.perDirect ? 'Contract per direct beëindigd!' : 'Contract einddatum aangepast!')

                this.$emit('reload')

                if(response.data == 'success'){
                    if (!!this.hasAccess) {
                        this.showAccessMessage = true
                    } else {
                        this.$emit('closeEndContractModal')
                    }
                }

                // If there is borg paid in the contract show the credit invoice modal.
                if(Array.isArray(response.data) && response.data.length) {
                    this.$emit('showCreditInvoiceModal', response.data)

                    if (!!this.hasAccess) {
                        this.showAccessMessage = true
                    } else {
                        setTimeout(() => this.$emit('closeEndContractModal'), 500)
                    }
                }
            }).catch((error) => {
                if(_.has(error, 'response.data.error') && error.response.data.error == 'not-org-admin'){
                    this.showError('Opslaan niet mogelijk, enkel een administrator is gemachtigd om deze gegevens aan te passen.')
                } else {
                    this.showError('Daar gaat iets fout! Probeer het opnieuw!')
                }

                this.$emit('closeEndContractModal')
            }).then(() => {
                setTimeout(() => this.loading = false, 500)
            })
        }
    },

    watch: {
        show(newVal){
            if(!!newVal){
                // Reset checkboxes
                this.perDirect = true
                this.cancelInvoices = true
                this.cancelAllInvoices = false

                this.setDates()
            }
        },

        perDirect(newVal) {
            if(newVal){
                this.endDate = moment(new Date()).format('YYYY-MM-DD')
            } else {
                this.setDates()
            }

            this.cancelAllInvoices = false
            this.cancelInvoices = true
        },

        cancelInvoices(newVal) {
            if(newVal) {
                this.cancelAllInvoices = false
            }
        },
        cancelAllInvoices(newVal) {
            if(newVal) {
                this.cancelInvoices = false
            }
        },

        endDate(newVal) {
            if(!_.isNull(newVal)){
                this.getUnpaidInvoices()
            }
        }
    }
}
</script>